/** Backups (v1) over the ALREADY-DEPLOYED backend (flipbase-clone/backend
 * routes/backup.py, /api/backup). Status + verify are reads; Run triggers a
 * server-side dump of the database to the connected Google Drive (non-
 * destructive — it only writes a COPY out; it never touches live data). No
 * backend changes. */
import { backendFetch } from "../../lib/backend";

export interface BackupStatus {
  driveConnected: boolean;
  last: { name: string; when: string; tables: number } | null;
  raw: Record<string, unknown>;
}

const s = (v: unknown): string => (typeof v === "string" ? v : "");
const num = (v: unknown): number => { const n = typeof v === "number" ? v : parseInt(String(v ?? ""), 10); return Number.isFinite(n) ? n : 0; };

export async function fetchBackupStatus(token: string | null, signal?: AbortSignal): Promise<BackupStatus> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/backup/status", { timeoutMs: 30000, ...(signal ? { signal } : {}) });
  const lastRaw = j["last"];
  let last: BackupStatus["last"] = null;
  if (lastRaw && typeof lastRaw === "object" && !Array.isArray(lastRaw)) {
    const o = lastRaw as Record<string, unknown>;
    last = { name: s(o["name"]), when: s(o["date"]) || s(o["when"]) || s(o["created_at"]), tables: num(o["tables"]) || num(o["count"]) };
  }
  return { driveConnected: j["drive_connected"] === true, last, raw: j };
}

/** Run a backup now. Writes a dated dump to Drive; returns the backend summary. */
export async function runBackup(token: string | null): Promise<{ ok: boolean; raw: Record<string, unknown> }> {
  // a full run (35 table dumps + 35 Drive uploads) measured 3.5 min live on
  // 2026-08-25 — the old 120s timeout reported failure while the backup
  // finished fine behind it
  const j = await backendFetch<Record<string, unknown>>(token, "/api/backup/run", { method: "POST", body: {}, timeoutMs: 360000 });
  return { ok: j["ok"] === true, raw: j };
}

/** Verify a backup's integrity (latest when date omitted). */
export async function verifyBackup(token: string | null, date = ""): Promise<{ ok: boolean; raw: Record<string, unknown> }> {
  const q = date ? "?date=" + encodeURIComponent(date) : "";
  // verify re-downloads every file from Drive — measured ~2 min live
  const j = await backendFetch<Record<string, unknown>>(token, "/api/backup/verify" + q, { timeoutMs: 240000 });
  return { ok: j["ok"] === true, raw: j };
}

// ---- state snapshots + guarded restore (parity #5, 2026-07-23) ---------------
// Backed by /api/state-history (list, admin-only) and /restore. Restore is the
// dangerous one — see SnapshotsCard for the double guard (typed RESTORE +
// the database mass-tombstone trigger that blocks oversized reverts).

export interface StateSnapshot {
  id: string;
  savedAt: string;
  /** blob record total (counts.total) — the legacy tripwire number */
  total: number | null;
  /** exact rec_* live-row total (counts.tables_total, present since 2026-07-23) */
  tablesTotal: number | null;
}

export async function fetchSnapshots(token: string | null): Promise<StateSnapshot[]> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/state-history?limit=40", { timeoutMs: 30000 });
  const rows = Array.isArray(j["snapshots"]) ? (j["snapshots"] as unknown[]) : [];
  const out: StateSnapshot[] = [];
  for (const r of rows) {
    if (r === null || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    const id = typeof o["id"] === "string" || typeof o["id"] === "number" ? String(o["id"]) : "";
    if (!id) continue;
    const counts = o["counts"] !== null && typeof o["counts"] === "object" && !Array.isArray(o["counts"]) ? (o["counts"] as Record<string, unknown>) : {};
    out.push({
      id,
      savedAt: s(o["saved_at"]) ?? "",
      total: typeof counts["total"] === "number" ? (counts["total"] as number) : null,
      tablesTotal: typeof counts["tables_total"] === "number" ? (counts["tables_total"] as number) : null,
    });
  }
  return out;
}

export async function restoreSnapshot(token: string | null, id: string): Promise<Record<string, unknown>> {
  return backendFetch<Record<string, unknown>>(token, "/api/state-history/restore", {
    method: "POST",
    timeoutMs: 120000,
    body: { id },
  });
}

export function fmtSnapWhen(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso || "—";
  return new Date(t).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
